import React from 'react';
import {
    View, Text, TouchableOpacity, StyleSheet,
} from 'react-native';
import { InputMode } from '@eduwrite/shared';

const MODE_CONFIG: Record<InputMode, { label: string; icon: string; color: string }> = {
    canvas: { label: 'Write', icon: '✏️', color: '#1890FF' },
    keyboard: { label: 'Type', icon: '⌨️', color: '#52C41A' },
    speech: { label: 'Speak', icon: '🎤', color: '#FA8C16' },
    numpad: { label: 'Numbers', icon: '🔢', color: '#722ED1' },
    word_tap: { label: 'Tiles', icon: '🧩', color: '#13C2C2' },
    choice_tap: { label: 'Choose', icon: '👆', color: '#EB2F96' },
    concrete_manipulative: { label: 'Counters', icon: '🍎', color: '#10B981' },
    drag_sort: { label: 'Sort', icon: '📦', color: '#8B5CF6' },
    grid_sequence: { label: 'Steps', icon: '🤖', color: '#F59E0B' },
};

interface InputModeSwitcherProps {
    allowedModes: InputMode[];
    activeMode: InputMode;
    onSelectMode: (mode: InputMode) => void;
}

/**
 * Horizontal mode-switcher bar for the student activity screen.
 *
 * Shows only the modes the teacher has allowed for this activity.
 * Large touch targets (56px+) for tablet use by young children.
 */
export const InputModeSwitcher: React.FC<InputModeSwitcherProps> = ({
    allowedModes,
    activeMode,
    onSelectMode,
}) => {
    if (allowedModes.length <= 1) return null;

    return (
        <View style={styles.container}>
            <View style={styles.row}>
                {allowedModes.map((mode) => {
                    const config = MODE_CONFIG[mode];
                    const isActive = mode === activeMode;
                    return (
                        <TouchableOpacity
                            key={mode}
                            activeOpacity={0.7}
                            onPress={() => onSelectMode(mode)}
                            style={[
                                styles.chip,
                                isActive && { backgroundColor: config.color + '20', borderColor: config.color },
                            ]}
                            accessibilityLabel={config.label}
                            accessibilityRole="button"
                            accessibilityHint={`Double tap to switch to ${config.label} mode`}
                            accessibilityState={{ selected: isActive }}
                        >
                            <Text style={styles.chipIcon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{config.icon}</Text>
                            <Text
                                allowFontScaling={true}
                                maxFontSizeMultiplier={1.5}
                                style={[
                                    styles.chipLabel,
                                    isActive && { color: config.color, fontWeight: '800' },
                                ]}
                            >
                                {config.label}
                            </Text>
                        </TouchableOpacity>
                    );
                })}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        paddingHorizontal: 12,
        paddingVertical: 8,
    },
    row: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    chip: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: 14,
        backgroundColor: 'rgba(255,255,255,0.06)',
        borderWidth: 2,
        borderColor: 'rgba(255,255,255,0.1)',
        minHeight: 56,
    },
    chipIcon: {
        fontSize: 20,
        marginRight: 8,
    },
    chipLabel: {
        fontSize: 16,
        fontWeight: '700',
        color: '#94a3b8',
    },
});
